import type { Component} from 'vue';
import { createApp, h } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';

export const slugify = (str: string) => str ? str.replace(/\W+/g, '-').toLowerCase() : undefined;

export const formatMoney = (amount = 0, maxDigits = 2, minDigits = 2, currency = 'EUR'): string => {
  const maximumFractionDigits = maxDigits;
  let minimumFractionDigits = minDigits;

  if (minimumFractionDigits > maximumFractionDigits) {
    minimumFractionDigits = maximumFractionDigits;
  }

  return Intl.NumberFormat('nl-NL', {
    style: 'currency',
    minimumFractionDigits,
    maximumFractionDigits,
    currency,
  })
    .format(!amount ? 0 : amount)
    .replace(/([,.])00$/, '$1-');
};

export const formatPercentage = (value = 0, maxDigits = 2, minDigits = 0) =>
  Intl.NumberFormat('nl-NL', {
    style: 'percent',
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits,
  }).format(value / 100);

export const formatMonthsToYears = (months: number) => `${months / 12} jaar`;

export const formatMonths = (months: number | null = null): string => {
  if (months === null) return 'n.n.b.';

  return `${months} maand${months > 1 ? 'en' : ''}`;
};

export const formatDate = (date: string) => dayjs(date).format('YYYY-MM-DD');
export const formatDateDutch = (date: string) => dayjs(date).format('DD-MM-YYYY');
export const formatDateLong = (date: string) => dayjs(date).locale('nl').format('D MMMM YYYY');
export const formatDateLongWithTime = (date: string) => dayjs(date).locale('nl').format('D MMMM YYYY HH:mm');

export const randomString = (lenght: number): string => {
  const chars = [...'0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'];

  return [...Array.from({length: lenght})].map(() =>
    chars[Math.floor(Math.random() * chars.length)]).join('');
}

// Minimum length
export const isMinLength = (value, [min]) => value?.length >= min
  || `Het minimale aantal tekens van dit veld is ${min}`;

// Special character
export const containsSpecialChar = value => /[!"#$%&'()*+,./:;<=>?@[\\\]^_{|}~-]+/.test(value)
  || 'Moet minimaal een speciaal teken bevatten';

// Number
export const containsNumber = value => (!!value && /(?=.*\d)/.test(value))
  || 'Moet minimaal een cijfer bevatten';

// Capital letter
export const containsCapital = value => (!!value && /(?=.*[A-Z])/.test(value))
  || 'Moet minimaal een hoofdletter bevatten';

// Lowercase letter
export const containsLowercase = value => (!!value && /(?=.*[a-z])/.test(value))
  || 'Moet minimaal een klein letter bevatten';

export const formatIBAN = (iban: string) => {
  const formattedIBAN = iban.toUpperCase().match(/.{1,4}/g);
  return formattedIBAN?.join(' ');
};
export const getComponentHtml = (component: Component, props: Record<string | number, unknown>) => {
  const tempApp = createApp({
    render: () => h(component, props),
  });

  const el = document.createElement('div');
  const mountedApp = tempApp.mount(el);

  return mountedApp.$el.outerHTML;
};
export const phoneRegExp = /^(?:\+\d{11,12}|0\d{9,10}|[1-9]\d{9,10})$/;
export const emailRegExp = /^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([\dA-Za-z]+\.)+[A-Za-z]{2,}))$/;

export const getFlagEmoji = (landCode: string) => {
  const codePoints = [...landCode.toUpperCase()].map(char => 127_397 + char.charCodeAt(0));

  return String.fromCodePoint(...codePoints);
};

export function decodeHtml(html: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
}
